/* eslint-disable-next-line  no-unused-vars */
import React, { createElement, useContext, useEffect, useState } from "react";

import {
  Container,
  TextField,
  Button,
  MenuItem,
  Box,
  Select,
  InputLabel,
  FormControl,
  FormHelperText,
  Alert,
  IconButton,
  Tooltip,
  Avatar,
} from "@mui/material";

import ViewerContext from "../../context/ViewerContext";
import ModalDialog from "./ModalDialog";
import PropTypes from "prop-types";
import axios from "axios";
import { CheckCircleOutline, InfoOutlined } from "@mui/icons-material";
import loaderHelper from "../loader/loaderHelper";
import StationConfiguration from "./StationConfiguration";

export const satelliteConstellations = [
  { name: "Beidou", id: "beidou" },
  { name: "Eutelsat", id: "eutelsat" },
  { name: "Galileo", id: "galileo" },
  { name: "Geostationary (GEO)", id: "geo" },
  { name: "GLONASS (GLO-OPS)", id: "glo-ops" },
  { name: "GNSS", id: "gnss" },
  { name: "Globalstar", id: "globalstar" },
  { name: "Iridium", id: "iridium" },
  { name: "OneWeb", id: "oneweb" },
  { name: "SES", id: "ses" },
  { name: "Spire", id: "spire" },
  { name: "Swarm", id: "swarm" },
  { name: "TDRSS", id: "tdrss" },
];

const baseUrl = process.env.API_GATEWAY || "https://satmon.fly.dev";

export default function ConstellationConfiguration(props) {
  const { openDialog, setOpenDialog } = props;
  const [openStationConfigDialog, setStationConfigDialog] = useState(false);
  const { constellationName, setConstellationName } = useContext(ViewerContext);
  const [selectedConstellation, setSelectedConstellation] =
    useState(constellationName);
  const [isConfigExist, setIsConfigExist] = useState();
  const [refreshApplication, setRefreshApplication] = useState(false);
  const [messageOption, setMessageOption] = useState({
    show: false,
    severity: "success",
    messageText: "",
  });

  const [option, setOption] = useState({
    celestrak_id: "",
    freq_hz: "",
    polarization: "",
    bandwidth_hz: "",
    eirp_dbw: "",
  });

  useEffect(() => {
    console.log(messageOption);
  }, [messageOption]);

  useEffect(() => {
    if (openDialog) getConstellationParams(constellationName.id);
  }, [openDialog]);

  const sortedSatellites = satelliteConstellations.sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  const showAlerMessage = (text, severity) => {
    setMessageOption({
      ...messageOption,
      severity: severity,
      messageText: text,
      show: true,
    });
  };

  const getConstellationParams = async (constellationId) => {
    loaderHelper().open();
    showAlerMessage("Fetching configuration for " + constellationId, "info");
    setOption({
      celestrak_id: constellationId ? constellationId : "",
      freq_hz: "",
      polarization: "",
      bandwidth_hz: "",
      eirp_dbw: "",
    });

    try {
      let url = `${baseUrl}/constellation-params`;
      if (constellationId) url += `/${constellationId}`;

      const response = await fetch(url);
      setIsConfigExist(response.ok);
      if (!response.ok) {
        showAlerMessage(
          "No configuration found for " + constellationId,
          "warning"
        );

        loaderHelper().close();
        return;
      }
      const result = await response.json();
      let data = result;

      if (!constellationId) {
        data = result[result.length - 1];
      }
      setOption({
        celestrak_id: data.celestrak_id,
        freq_hz: data.freq_hz / 1000000,
        polarization: data.polarization,
        bandwidth_hz: data.bandwidth_hz / 1000000,
        eirp_dbw: data.eirp_dbw,
      });

      showAlerMessage("Configuration listed for " + constellationId, "success");
    } catch (error) {
      showAlerMessage(
        "Failed to fetch configuration data for " + constellationId,
        "error"
      );
      console.log(error);
    } finally {
    }

    loaderHelper().close();
  };

  const onCloseFunc = () => {
    setOpenDialog(false);
    loaderHelper().close();
    if (refreshApplication) {
      console.log("App Refresh");
      setConstellationName({ ...constellationName });
    }
  };

  const handleConstellationChange = (e) => {
    const { name, value } = e.target;
    getConstellationParams(value);

    const selectedCons = satelliteConstellations.find(
      (satellite) => satellite.id === value
    );
    setSelectedConstellation(selectedCons);
  };

  const handleChange = (e) => {
    let { name, value } = e.target;

    if ((name === "freq_hz" || name === "bandwidth_hz") && value < 0) return;
    if (name === "eirp_dbw" && (value < -99999.99 || value > 99999.99)) return;

    if (name == "freq_hz" || name === "bandwidth_hz") {
      const freqDecimal = value.split(".");
      if (freqDecimal.length == 2) {
        if (freqDecimal[1].length > 5) {
          value = parseFloat(value).toFixed(6);
        }
      }
    }
    setOption({
      ...option,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    try {
      loaderHelper().open();
      showAlerMessage(
        "Saving configuration data for " + option.celestrak_id,
        "info"
      );

      let response;
      if (isConfigExist) {
        response = await axios.put(
          `${baseUrl}/constellation-params/${option.celestrak_id}`,
          {
            freq_hz: parseFloat(option.freq_hz).toFixed(6) * 1000000,
            polarization: option.polarization,
            bandwidth_hz: parseFloat(option.bandwidth_hz).toFixed(6) * 1000000,
            eirp_dbw: option.eirp_dbw,
          }
        );
      } else {
        response = await axios.post(`${baseUrl}/constellation-params`, option);
      }

      setOpenDialog(false);

      showAlerMessage(
        "Configuration succesfully saved for " + option.celestrak_id,
        "success"
      );

      console.log("Success:", response.data);
    } catch (error) {
      console.error("Error:", error);
      showAlerMessage(
        "Error while saving configuration " + option.celestrak_id,
        "error"
      );
    }

    setConstellationName({ ...selectedConstellation });
    loaderHelper().close();
  };

  const onOpenStationFunc = () => {
    setStationConfigDialog(true);
  };

  return (
    <>
      <ModalDialog
        dialogTitle={"Constellation Configuration Screen"}
        openModal={openDialog}
        setOpenModal={setOpenDialog}
        onSubmit={handleSubmit}
        submitButtonText={"Save"}
        onCancel={onCloseFunc}
        dialogContent={
          <Container maxWidth="sm">
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
              <div className="constellation-select">
                <FormControl fullWidth>
                  <InputLabel id="constellation-select-label">
                    Constellation ID
                  </InputLabel>
                  <Select
                    name="celestrak_id"
                    labelId="constellation-select-label"
                    value={option.celestrak_id}
                    onChange={handleConstellationChange}
                  >
                    {sortedSatellites.map((satellite) => (
                      <MenuItem key={satellite.id} value={satellite.id}>
                        {satellite.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Tooltip title="Add Rx Station" arrow>
                  <IconButton onClick={onOpenStationFunc}>
                    <Avatar
                      sx={{ width: "44px", height: "44px" }}
                      src="./icons/space-station2.png"
                    >
                      Add RX Station
                    </Avatar>
                  </IconButton>
                </Tooltip>
              </div>
              <TextField
                fullWidth
                name="freq_hz"
                label="Frequency (MHz)"
                type="number"
                value={option.freq_hz}
                onChange={handleChange}
                margin="normal"
              />
              <TextField
                fullWidth
                select
                name="polarization"
                label="Polarization"
                value={option.polarization}
                onChange={handleChange}
                margin="normal"
              >
                <MenuItem value="linear">LINEAR</MenuItem>
                <MenuItem value="circular">CIRCULAR</MenuItem>
                <MenuItem value="dual">DUAL</MenuItem>
              </TextField>
              <TextField
                fullWidth
                name="bandwidth_hz"
                label="Bandwidth (MHz)"
                type="number"
                value={option.bandwidth_hz}
                onChange={handleChange}
                margin="normal"
              />
              <TextField
                fullWidth
                name="eirp_dbw"
                label="EIRP (dBW)"
                type="number"
                value={option.eirp_dbw}
                onChange={handleChange}
                margin="normal"
              />
            </Box>
            {messageOption.show && (
              <Alert
                icon={<InfoOutlined fontSize="inherit" />}
                severity={messageOption.severity}
              >
                {messageOption.messageText}
              </Alert>
            )}
          </Container>
        }
      />

      {openStationConfigDialog && (
        <StationConfiguration
          openDialog={openStationConfigDialog}
          setOpenDialog={setStationConfigDialog}
          celestrakId={option.celestrak_id}
          refreshFunction={setRefreshApplication}
        />
      )}
    </>
  );
}

ConstellationConfiguration.propTypes = {
  openDialog: PropTypes.bool.isRequired,
  setOpenDialog: PropTypes.func.isRequired,
};
